import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs';

import {
  authorizedData,
  portalAcceptVerification,
  portalAuthorizedBooking,
  portalAuthorizedOrder,
  portalNewLead,
  portalSendVerificationEmail,
  updatePassenger,
  portalReferralProgram as portalReferralProgramQuery,
} from '@app/services/api/api.graphql';
import { protectedBaseUrl } from '@app/services/api/api.service';
import {
  AuthorizedDataNode,
  NewLead,
  PortalBookingNode,
  PortalOrderNode,
  PortalUserVerificationNode,
  PortalReferralProgramNode,
} from '@app/services/api/api.types';
import { FormError } from '@app/pages/client-traveler-page/client-traveler-page.component';

@Injectable({
  providedIn: 'root',
})
export class ApiProtectedService {
  constructor(private httpClient: HttpClient) {}

  authorizedData(variables: { portalLink: string }) {
    const query = authorizedData();

    return this.httpClient
      .post<{
        data: { authorizedData: AuthorizedDataNode | null };
      }>(protectedBaseUrl, { query, variables })
      .pipe(map((response) => response.data?.authorizedData ?? null));
  }

  portalSendVerificationEmail(variables: { portalLink: string }) {
    const query = portalSendVerificationEmail();

    return this.httpClient
      .post<{
        data: { portalSendVerificationEmail: { ok: boolean; result: AuthorizedDataNode } };
      }>(protectedBaseUrl, { query, variables })
      .pipe(map((response) => response.data.portalSendVerificationEmail));
  }

  portalAcceptVerification(variables: { portalLink: string; verificationCode: string }) {
    const query = portalAcceptVerification();

    return this.httpClient.post<{
      data: { portalAcceptVerification: { ok: boolean; result: AuthorizedDataNode } };
      errors: { message: string }[];
    }>(protectedBaseUrl, { query, variables });
  }

  portalNewLead(input: NewLead) {
    const query = portalNewLead();

    return this.httpClient.post<{
      data: { portalNewLead: { ok: boolean; portalLink: string; errors: { message: string }[] } };
      errors: { message: string }[];
    }>(protectedBaseUrl, { query, variables: { input } });
  }

  portalAuthorizedBooking(portalLink: string) {
    const query = portalAuthorizedBooking();

    return this.httpClient.post<{
      data: {
        portalAuthorizedBooking: {
          booking: PortalBookingNode;
          verification: PortalUserVerificationNode;
        };
      };
      errors: { message: string }[];
    }>(protectedBaseUrl, { query, variables: { portalLink } });
  }

  portalAuthorizedOrder(portalLink: string) {
    const query = portalAuthorizedOrder();

    return this.httpClient.post<{
      data: {
        portalAuthorizedOrder: {
          order: PortalOrderNode;
          verification: PortalUserVerificationNode;
        };
      };
      errors: { message: string }[];
    }>(protectedBaseUrl, { query, variables: { portalLink } });
  }

  portalReferralProgram() {
    const query = portalReferralProgramQuery();

    return this.httpClient
      .post<{
        data: { portalReferralProgram: PortalReferralProgramNode | null };
        errors?: { message: string }[];
      }>(protectedBaseUrl, { query })
      .pipe(map((response) => response.data?.portalReferralProgram ?? null));
  }

  updatePassenger(variables: {
    inputData: {
      id: string;
      specialAssistanceId?: string;
      petsId?: string;
      mealPreferencesId?: string;
      seatPreferencesId?: string;
      phones?: string[];
      emails?: string[];
      globalEntry?: string;
      knownTravelerNumber?: string;
      passport?: { number: string; expiration: Date };
      frequentFlyerNumbers?: { number: string; airlineId: string }[];
    };
  }) {
    const query = updatePassenger();

    return this.httpClient.post<{
      data: { updatePassenger: { ok: boolean; errors: string } };
      errors: FormError[];
    }>(protectedBaseUrl, { query, variables });
  }
}
